import React, { useCallback, useState, useRef, useEffect } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { ColumnModel, CardFilters } from '@/features/crm/types';
import { useStepColumnCards } from '@/features/crm/api/listCardsByStep';
import ColumnCards from '@/features/crm/ColumnCards';

type DraggedItemProps = {
  columnId: number;
  itemId: number;
}

interface ColumnWithInfiniteScrollProps {
  column: ColumnModel;
  draggedItem: DraggedItemProps | null;
  handleDragOver: (e: React.DragEvent<HTMLDivElement>) => void;
  handleDrop: (e: React.DragEvent<HTMLDivElement>, targetColumnId: number, targetItemId: number | null) => void;
  handleDragEnd: () => void;
  handleDragStart: (e: React.DragEvent<HTMLDivElement>, columnId: number, itemId: number) => void;
  filterName?: string;
  isUpdating?: boolean;
  filters: CardFilters;
}

export const ColumnWithInfiniteScroll: React.FC<ColumnWithInfiniteScrollProps> = ({
  column,
  draggedItem,
  handleDragOver,
  handleDrop,
  handleDragEnd,
  handleDragStart,
  filterName,
  isUpdating,
  filters
}) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const observerTarget = useRef<HTMLDivElement>(null);

  // Busca os cards dessa coluna com paginação
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    error
  } = useStepColumnCards(column.id, filters);

  // Flatten all pages into a single array
  const allCards = data?.pages.flatMap(page => page.results) ?? [];

  // Cria uma coluna "virtual" com os cards carregados
  const columnWithCards: ColumnModel = {
    ...column,
    cards: allCards
  };

  // Intersection Observer para detectar quando chegar no fim da lista
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      {
        root: scrollContainerRef.current,
        rootMargin: '100px',
        threshold: 0.1
      }
    );

    if (observerTarget.current) {
      observer.observe(observerTarget.current);
    }

    return () => {
      if (observerTarget.current) {
        observer.unobserve(observerTarget.current);
      }
    };
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  if (error) {
    return (
      <Box p={2}>
        <Typography color="error" variant="body2">
          Erro ao carregar cards
        </Typography>
      </Box>
    );
  }

  return (
    <Box 
      ref={scrollContainerRef}
      sx={{ 
        height: '100%', 
        overflowY: 'auto',
        overflowX: 'hidden'
      }}
    >
      {isLoading && allCards.length === 0 ? (
        <Box display="flex" justifyContent="center" p={2}>
          <CircularProgress size={20} />
        </Box>
      ) : (
        <>
          <ColumnCards
            step={columnWithCards}
            draggedItem={draggedItem}
            handleDragOver={handleDragOver}
            handleDrop={handleDrop}
            handleDragEnd={handleDragEnd}
            handleDragStart={handleDragStart}
            filterName={filterName}
            isUpdating={isUpdating}
          />
          
          {/* Trigger para carregar mais */}
          <div ref={observerTarget} style={{ height: '20px', width: '100%' }} />
          
          {isFetchingNextPage && (
            <Box display="flex" justifyContent="center" p={2}>
              <CircularProgress size={20} />
            </Box>
          )}
        </>
      )}
    </Box>
  );
};
